import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  CircularProgress,
  Chip,
  LinearProgress,
  Tabs,
  Tab
} from '@mui/material';
import {
  People as PeopleIcon,
  Event as EventIcon,
  Photo as PhotoIcon,
  Assignment as AssignmentIcon,
  TrendingUp as TrendingUpIcon
} from '@mui/icons-material';

interface UsageAnalytics {
  guests: {
    total_guests: number;
    active_guests: number;
    new_guests_week: number;
  };
  events: {
    total_events: number;
    guests_with_registrations: number;
    avg_registrations_per_event: number;
  };
  media: {
    total_media: number;
    approved_media: number;
    featured_media: number;
    new_media_week: number;
  };
  requests: {
    event_change_requests: number;
    size_change_requests: number;
    pending_requests: number;
    approved_requests: number;
  };
  generatedAt: string;
}

interface EngagementAnalytics {
  eventEngagement: Array<{
    event_date: string;
    event_name: string;
    registered_guests: number;
    max_capacity: number | null;
    capacity_percentage: number | null;
  }>;
  mediaEngagement: Array<{
    upload_date: string;
    uploads_count: number;
    approved_count: number;
  }>;
  requestActivity: Array<{
    request_date: string;
    request_type: string;
    request_count: number;
    approved_count: number;
  }>;
  generatedAt: string;
}

interface PinAnalytics {
  overview: {
    timeframe: string;
    generatedAt: string;
  };
  metrics: {
    totalPinLogins: number;
    successfulLogins: number;
    failedLogins: number;
    successRate: number;
    uniqueUsers: number;
    averageAttemptsPerUser: number;
  };
  securityMetrics: {
    accountsLocked: number;
    accountsUnlocked: number;
    activelyLockedAccounts: number;
    multipleFailureGuests: number;
    suspiciousActivityCount: number;
  };
  usagePatterns: {
    daily: Array<{
      date: string;
      successful: number;
      failed: number;
      total: number;
      successRate: number;
    }>;
    hourly: Array<{
      hour: number;
      loginCount: number;
      successRate: number;
    }>;
  };
  securityAlerts: {
    alerts: Array<{
      id: string;
      guestId: string;
      guestEmail: string;
      alertType: string;
      severity: string;
      description: string;
      timestamp: string;
      resolved: boolean;
    }>;
    summary: {
      total: number;
      critical: number;
      high: number;
      medium: number;
    };
  };
  adminActivity: Array<{
    adminId: string;
    pinsGenerated: number;
    pinsChanged: number;
    accountsUnlocked: number;
    totalActions: number;
    lastActivity: string;
  }>;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`analytics-tabpanel-${index}`}
      aria-labelledby={`analytics-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export const AnalyticsDashboard: React.FC = () => {
  const [currentTab, setCurrentTab] = useState(0);
  const [usageData, setUsageData] = useState<UsageAnalytics | null>(null);
  const [engagementData, setEngagementData] = useState<EngagementAnalytics | null>(null);
  const [pinAnalytics, setPinAnalytics] = useState<PinAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadAnalyticsData();
  }, []);

  const loadAnalyticsData = async () => {
    try {
      // Use the ApiService which has the correct API Gateway URL
      const { ApiService } = await import('../services/api');
      
      const [usageResult, engagementResult, pinAnalyticsResult] = await Promise.all([
        ApiService.getUsageAnalytics(),
        ApiService.getEngagementAnalytics(),
        ApiService.getPinAnalyticsDashboard(7)
      ]);

      if (usageResult.success) {
        setUsageData(usageResult.data);
      }

      if (engagementResult.success) {
        setEngagementData(engagementResult.data);
      }

      if (pinAnalyticsResult.success) {
        setPinAnalytics(pinAnalyticsResult.data);
      }

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const getCapacityColor = (percentage: number | null) => {
    if (!percentage) return 'default';
    if (percentage >= 90) return 'error';
    if (percentage >= 70) return 'warning';
    return 'success';
  };

  const getApprovalRate = (approved: number, total: number) => {
    if (total === 0) return 0;
    return Math.round((approved / total) * 100);
  };

  const getSeverityColor = (severity: string) => {
    switch (severity.toLowerCase()) {
      case 'critical': return 'error';
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'info';
      default: return 'default';
    }
  };

  const getSuccessRateColor = (rate: number) => {
    if (rate >= 95) return 'success';
    if (rate >= 85) return 'warning';
    return 'error';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Analytics Dashboard
      </Typography>

      <Paper sx={{ width: '100%' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={currentTab} onChange={(e, newValue) => setCurrentTab(newValue)}>
            <Tab label="Usage Overview" />
            <Tab label="Engagement Metrics" />
            <Tab label="PIN Analytics" />
          </Tabs>
        </Box>

        {/* Usage Overview Tab */}
        <TabPanel value={currentTab} index={0}>
          {usageData && (
            <Box>
              {/* Key Metrics Cards */}
              <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent>
                      <Box display="flex" alignItems="center" mb={2}>
                        <PeopleIcon color="primary" sx={{ mr: 1 }} />
                        <Typography variant="h6">Guest Activity</Typography>
                      </Box>
                      <Typography variant="h4" color="primary">
                        {usageData.guests.total_guests}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total guests
                      </Typography>
                      <Box mt={1}>
                        <Typography variant="body2">
                          {usageData.guests.active_guests} active
                        </Typography>
                        <Typography variant="body2" color="success.main">
                          +{usageData.guests.new_guests_week} this week
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent>
                      <Box display="flex" alignItems="center" mb={2}>
                        <EventIcon color="secondary" sx={{ mr: 1 }} />
                        <Typography variant="h6">Event Engagement</Typography>
                      </Box>
                      <Typography variant="h4" color="secondary">
                        {usageData.events.guests_with_registrations}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Guests with registrations
                      </Typography>
                      <Box mt={1}>
                        <Typography variant="body2">
                          {usageData.events.total_events} total events
                        </Typography>
                        <Typography variant="body2">
                          {Math.round(Number(usageData.events.avg_registrations_per_event) || 0)} avg per event
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent>
                      <Box display="flex" alignItems="center" mb={2}>
                        <PhotoIcon color="success" sx={{ mr: 1 }} />
                        <Typography variant="h6">Media Activity</Typography>
                      </Box>
                      <Typography variant="h4" color="success.main">
                        {usageData.media.total_media}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total uploads
                      </Typography>
                      <Box mt={1}>
                        <Typography variant="body2">
                          {getApprovalRate(usageData.media.approved_media, usageData.media.total_media)}% approved
                        </Typography>
                        <Typography variant="body2" color="success.main">
                          +{usageData.media.new_media_week} this week
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent>
                      <Box display="flex" alignItems="center" mb={2}>
                        <AssignmentIcon color="warning" sx={{ mr: 1 }} />
                        <Typography variant="h6">Request Activity</Typography>
                      </Box>
                      <Typography variant="h4" color="warning.main">
                        {usageData.requests.pending_requests}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Pending requests
                      </Typography>
                      <Box mt={1}>
                        <Typography variant="body2">
                          {usageData.requests.event_change_requests} event changes
                        </Typography>
                        <Typography variant="body2">
                          {usageData.requests.size_change_requests} size changes
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Request Approval Rate */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Request Approval Rate
                  </Typography>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Box flex={1}>
                      <Typography variant="body2" color="text.secondary">
                        Overall approval rate
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={getApprovalRate(
                          usageData.requests.approved_requests,
                          usageData.requests.approved_requests + usageData.requests.pending_requests
                        )}
                        sx={{ height: 8, borderRadius: 4 }}
                      />
                    </Box>
                    <Typography variant="h6">
                      {getApprovalRate(
                        usageData.requests.approved_requests,
                        usageData.requests.approved_requests + usageData.requests.pending_requests
                      )}%
                    </Typography>
                  </Box>
                </CardContent>
              </Card>

              <Typography variant="caption" color="text.secondary">
                Data generated at: {new Date(usageData.generatedAt).toLocaleString()}
              </Typography>
            </Box>
          )}
        </TabPanel>

        {/* Engagement Metrics Tab */}
        <TabPanel value={currentTab} index={1}>
          {engagementData && (
            <Box>
              {/* Event Engagement Table */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Event Engagement
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Event</TableCell>
                          <TableCell>Date</TableCell>
                          <TableCell align="right">Registered</TableCell>
                          <TableCell align="right">Capacity</TableCell>
                          <TableCell align="right">Fill Rate</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {engagementData.eventEngagement.map((event, index) => (
                          <TableRow key={index}>
                            <TableCell>{event.event_name}</TableCell>
                            <TableCell>
                              {new Date(event.event_date).toLocaleDateString()}
                            </TableCell>
                            <TableCell align="right">{event.registered_guests}</TableCell>
                            <TableCell align="right">
                              {event.max_capacity || 'Unlimited'}
                            </TableCell>
                            <TableCell align="right">
                              {event.capacity_percentage ? (
                                <Chip
                                  label={`${Math.round(event.capacity_percentage)}%`}
                                  size="small"
                                  color={getCapacityColor(event.capacity_percentage) as any}
                                />
                              ) : (
                                '-'
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>

              {/* Media Upload Trends */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Media Upload Trends (Last 30 Days)
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Date</TableCell>
                          <TableCell align="right">Uploads</TableCell>
                          <TableCell align="right">Approved</TableCell>
                          <TableCell align="right">Approval Rate</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {engagementData.mediaEngagement.slice(0, 10).map((day, index) => (
                          <TableRow key={index}>
                            <TableCell>
                              {new Date(day.upload_date).toLocaleDateString()}
                            </TableCell>
                            <TableCell align="right">{day.uploads_count}</TableCell>
                            <TableCell align="right">{day.approved_count}</TableCell>
                            <TableCell align="right">
                              <Chip
                                label={`${getApprovalRate(day.approved_count, day.uploads_count)}%`}
                                size="small"
                                color={
                                  getApprovalRate(day.approved_count, day.uploads_count) >= 80
                                    ? 'success'
                                    : getApprovalRate(day.approved_count, day.uploads_count) >= 60
                                    ? 'warning'
                                    : 'error'
                                }
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>

              {/* Request Activity Trends */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Request Activity (Last 30 Days)
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Date</TableCell>
                          <TableCell>Type</TableCell>
                          <TableCell align="right">Requests</TableCell>
                          <TableCell align="right">Approved</TableCell>
                          <TableCell align="right">Approval Rate</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {engagementData.requestActivity.slice(0, 15).map((activity, index) => (
                          <TableRow key={index}>
                            <TableCell>
                              {new Date(activity.request_date).toLocaleDateString()}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={activity.request_type.replace('_', ' ')}
                                size="small"
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell align="right">{activity.request_count}</TableCell>
                            <TableCell align="right">{activity.approved_count}</TableCell>
                            <TableCell align="right">
                              <Chip
                                label={`${getApprovalRate(activity.approved_count, activity.request_count)}%`}
                                size="small"
                                color={
                                  getApprovalRate(activity.approved_count, activity.request_count) >= 80
                                    ? 'success'
                                    : getApprovalRate(activity.approved_count, activity.request_count) >= 60
                                    ? 'warning'
                                    : 'error'
                                }
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>

              <Typography variant="caption" color="text.secondary">
                Data generated at: {new Date(engagementData.generatedAt).toLocaleString()}
              </Typography>
            </Box>
          )}
        </TabPanel>

        {/* PIN Analytics Tab */}
        <TabPanel value={currentTab} index={2}>
          {pinAnalytics && (
            <Box>
              {/* PIN Metrics Cards */}
              <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent>
                      <Box display="flex" alignItems="center" mb={2}>
                        <TrendingUpIcon color="primary" sx={{ mr: 1 }} />
                        <Typography variant="h6">PIN Authentication</Typography>
                      </Box>
                      <Typography variant="h4" color="primary">
                        {pinAnalytics.metrics.totalPinLogins}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total PIN logins
                      </Typography>
                      <Box mt={1}>
                        <Typography variant="body2" color="success.main">
                          {pinAnalytics.metrics.successfulLogins} successful
                        </Typography>
                        <Typography variant="body2" color="error.main">
                          {pinAnalytics.metrics.failedLogins} failed
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent>
                      <Box display="flex" alignItems="center" mb={2}>
                        <PeopleIcon color="success" sx={{ mr: 1 }} />
                        <Typography variant="h6">Success Rate</Typography>
                      </Box>
                      <Typography variant="h4" color={getSuccessRateColor(pinAnalytics.metrics.successRate) + '.main'}>
                        {pinAnalytics.metrics.successRate}%
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Authentication success
                      </Typography>
                      <Box mt={1}>
                        <Typography variant="body2">
                          {pinAnalytics.metrics.uniqueUsers} unique users
                        </Typography>
                        <Typography variant="body2">
                          {pinAnalytics.metrics.averageAttemptsPerUser} avg attempts/user
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent>
                      <Box display="flex" alignItems="center" mb={2}>
                        <AssignmentIcon color="warning" sx={{ mr: 1 }} />
                        <Typography variant="h6">Security Status</Typography>
                      </Box>
                      <Typography variant="h4" color="warning.main">
                        {pinAnalytics.securityMetrics.activelyLockedAccounts}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Locked accounts
                      </Typography>
                      <Box mt={1}>
                        <Typography variant="body2">
                          {pinAnalytics.securityMetrics.multipleFailureGuests} with multiple failures
                        </Typography>
                        <Typography variant="body2" color="error.main">
                          {pinAnalytics.securityMetrics.suspiciousActivityCount} suspicious activities
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent>
                      <Box display="flex" alignItems="center" mb={2}>
                        <EventIcon color="error" sx={{ mr: 1 }} />
                        <Typography variant="h6">Security Alerts</Typography>
                      </Box>
                      <Typography variant="h4" color="error.main">
                        {pinAnalytics.securityAlerts.summary.total}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Active alerts
                      </Typography>
                      <Box mt={1}>
                        <Typography variant="body2" color="error.main">
                          {pinAnalytics.securityAlerts.summary.critical} critical
                        </Typography>
                        <Typography variant="body2" color="warning.main">
                          {pinAnalytics.securityAlerts.summary.high} high priority
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Security Alerts */}
              {pinAnalytics.securityAlerts.alerts.length > 0 && (
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Recent Security Alerts
                    </Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Guest</TableCell>
                            <TableCell>Alert Type</TableCell>
                            <TableCell>Severity</TableCell>
                            <TableCell>Description</TableCell>
                            <TableCell>Time</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {pinAnalytics.securityAlerts.alerts.slice(0, 5).map((alert) => (
                            <TableRow key={alert.id}>
                              <TableCell>{alert.guestEmail}</TableCell>
                              <TableCell>
                                <Chip
                                  label={alert.alertType.replace('_', ' ')}
                                  size="small"
                                  variant="outlined"
                                />
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={alert.severity}
                                  size="small"
                                  color={getSeverityColor(alert.severity) as any}
                                />
                              </TableCell>
                              <TableCell>{alert.description}</TableCell>
                              <TableCell>
                                {new Date(alert.timestamp).toLocaleString()}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              )}

              {/* Daily Usage Patterns */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Daily PIN Usage Patterns (Last 7 Days)
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Date</TableCell>
                          <TableCell align="right">Successful</TableCell>
                          <TableCell align="right">Failed</TableCell>
                          <TableCell align="right">Total</TableCell>
                          <TableCell align="right">Success Rate</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {pinAnalytics.usagePatterns.daily.map((day) => (
                          <TableRow key={day.date}>
                            <TableCell>
                              {new Date(day.date).toLocaleDateString()}
                            </TableCell>
                            <TableCell align="right" sx={{ color: 'success.main' }}>
                              {day.successful}
                            </TableCell>
                            <TableCell align="right" sx={{ color: 'error.main' }}>
                              {day.failed}
                            </TableCell>
                            <TableCell align="right">{day.total}</TableCell>
                            <TableCell align="right">
                              <Chip
                                label={`${day.successRate}%`}
                                size="small"
                                color={getSuccessRateColor(day.successRate) as any}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>

              {/* Admin Activity */}
              {pinAnalytics.adminActivity.length > 0 && (
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Admin PIN Activity
                    </Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Admin ID</TableCell>
                            <TableCell align="right">PINs Generated</TableCell>
                            <TableCell align="right">PINs Changed</TableCell>
                            <TableCell align="right">Accounts Unlocked</TableCell>
                            <TableCell align="right">Total Actions</TableCell>
                            <TableCell>Last Activity</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {pinAnalytics.adminActivity.map((admin) => (
                            <TableRow key={admin.adminId}>
                              <TableCell>{admin.adminId}</TableCell>
                              <TableCell align="right">{admin.pinsGenerated}</TableCell>
                              <TableCell align="right">{admin.pinsChanged}</TableCell>
                              <TableCell align="right">{admin.accountsUnlocked}</TableCell>
                              <TableCell align="right">
                                <Chip
                                  label={admin.totalActions}
                                  size="small"
                                  color="primary"
                                />
                              </TableCell>
                              <TableCell>
                                {new Date(admin.lastActivity).toLocaleString()}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              )}

              <Typography variant="caption" color="text.secondary">
                PIN analytics for {pinAnalytics.overview.timeframe} - Generated at: {new Date(pinAnalytics.overview.generatedAt).toLocaleString()}
              </Typography>
            </Box>
          )}
        </TabPanel>
      </Paper>
    </Box>
  );
};